import type { Adobe } from 'eplayer-core'
import type { Player } from 'eplayer-core'
import type { UUID } from 'eplayer-core'

import type { IO } from '@common/io'
import { groupBy} from '@common/array.utils'
import { io } from '@common/io'
import { translateHttpErr } from '@common/io/http'
import { getMIMESubtype } from '@common/mime.utils'
import { abort } from 'process'

export type MediaUpload = {
  filename: string
  type: string
  size: number
  base64Content: string
  userData: Adobe.AdobeTimelineEvent[] | null
}

export type AssetManagerEntry = {
  objectId: UUID
  rawMediaId: string
  fileExt: string
  fileName: string
  checksum: string
  size: number
  durationMs: number
  height: number
  width: number
  frameCount: number
  frameWidth: number
  frameRate: number
  username: string
  src: string
  dateTime: string
  mediaHost: string
  userData: Adobe.AdobeTimelineEvent[] | null
}

export async function filesToMediaUploads(
    files: File[],
    io: Pick<IO, 'fileToBase64' | 'fileToUTF16' | 'strToBase64' | 'adobeEmbedFromAdobeJS' | 'adobeLabelsFromAdobeJS' | 'adobeStrFromJsFile'>,
  ) {
  const sortedFiles = files.slice(0).sort(
    (a, b) => a.name.localeCompare(b.name))
  const groups = groupBy(sortedFiles, file => {
    const ext = getMIMESubtype(file.type)
    return ext === "mpeg" ? "mp3" :
      ext === "javascript" ? "js" :
        ext
  })
  const paired: [File, File][] = []
  const unpairedJS: File[] = []
  const unpairedVideo: File[] = []
  const mediaUploads: MediaUpload[] = []
  const baseNameCounts = new Map<string, number>();

  let { mp4: videoFiles, js: jsFiles, mp3: mp3Files, ...restGroups }
    = groups

  jsFiles = jsFiles ?? []
  mp3Files = mp3Files ?? []
  restGroups = restGroups ?? {}
  videoFiles = videoFiles ?? []

  for (const jsFile of jsFiles) {
    const baseName = jsFile.name.split('.').slice(0, -1).join('.');
    baseNameCounts.set(baseName, (baseNameCounts.get(baseName) ?? 0) + 1);
    const matchingVideo = videoFiles.find(video => 
      video.name.split('.').slice(0, -1).join('.') === baseName)
    if (matchingVideo) {
      paired.push([jsFile, matchingVideo])
    } else {
      unpairedJS.push(jsFile)
    }
  }
  for (const videoFile of videoFiles) {
    const baseName = videoFile.name.split('.').slice(0, -1).join('.')
    baseNameCounts.set(baseName, (baseNameCounts.get(baseName) ?? 0) + 1);
    const isPair = paired.find(([,pairedVideo]) =>
      pairedVideo === videoFile)

    if (!isPair) unpairedVideo.push(videoFile)
  }

  for (const [baseName, count] of baseNameCounts) {
    if (count > 2) {
      throw new Error(`More than two files share the base name: ${baseName}`);
    }
  }

  // Load pairedJS and extract labels
  // Assign labels to paired video meta as keyframes
  for (const [jsFile, videoFile] of paired) {
    const { size } = videoFile
    const { name: filename } = videoFile
    const filetype = getMIMESubtype(videoFile.type)
    const basename = jsFile.name.split('.')
      .slice(0, -1)
      .join('.')
    const jsStr = await io.adobeStrFromJsFile(jsFile)
    const meta = io.adobeLabelsFromAdobeJS(basename, jsStr)
    const base64Content = await io.fileToBase64(videoFile)
    mediaUploads.push({ filename, size, base64Content, userData: meta, type: filetype })
  }

  for (const jsFile of unpairedJS) {
    const { size } = jsFile
    const { name: filename } = jsFile
    const filetype = 'js'
    const basename = filename.split('.')
      .slice(0, -1)
      .join('.')
    const jsStr = await io.adobeStrFromJsFile(jsFile)
    const base64Content = await io.strToBase64(jsStr)
    const { labels: meta } = await io.adobeEmbedFromAdobeJS(basename, jsStr, globalThis.createjs)
    mediaUploads.push({ filename, size, base64Content, userData: meta, type: filetype })
  }

  for (const videoFile of unpairedVideo) {
    const meta = null
    const { size } = videoFile;
    const { name: filename } = videoFile;
    const filetype = getMIMESubtype(videoFile.type)
    const base64Content = await io.fileToBase64(videoFile)
    mediaUploads.push({ filename, size, base64Content, userData: meta, type: filetype })
  }

  for (const mp3File of mp3Files) {
    const meta = null
    const { size } = mp3File
    const { name: filename } = mp3File
    const filetype = 'mp3'
    const base64Content = await io.fileToBase64(mp3File)
    mediaUploads.push({ filename, size, base64Content, userData: meta, type: filetype })
  }

  for (const group of Object.values(restGroups)) {
    for (const file of group) {
      const meta = null
      const { size } = file
      const { name: filename } = file
      const filetype = getMIMESubtype(file.type)
      const base64Content = await io.fileToBase64(file)
      mediaUploads.push({ filename, size, base64Content, userData: meta, type: filetype })
    }
  }

  return mediaUploads
}

export async function* syncMediaUploads(
    abortSignal: AbortSignal,
    mediaUploads: MediaUpload[],
    io: Pick<IO, 'httpPostJson' | 'cacheMediaInfo'>,
) {
  const endpoint = '/authoringtool/media'
  for (const upload of mediaUploads) {
    const filename = upload.filename
    try {
      const reply = await io.httpPostJson(endpoint, upload, {
        signal: abortSignal
      })
      if (!reply.ok) throw new Error(translateHttpErr(reply.status))
      const mediaInfo: AssetManagerEntry = await reply.json()
      yield [ filename, mediaInfo ] as const
    } catch(err) {
      if (abortSignal.aborted) return
      const msg = err instanceof Error ?
        `Failed to upload ${filename}: ${err.message}` :
        `Failed to upload ${filename}: ${String(err)}`
      yield [ filename, new Error(msg) ] as const
    }
  }
}

export async function* uploadMediaFiles(abortSignal: AbortSignal, ...files: File[]) {
  const mediaUploads = await filesToMediaUploads(files, io)
  for await (const [ filename, result ] of syncMediaUploads(abortSignal, mediaUploads, io)) {
    if (abortSignal.aborted) break
    const file = files.find(f => f.name === filename) as File
    yield [ file, result ] as const
  }
}
